/**
 * PSSP LABS — "PSSP Precision" theme
 * material-library.js — <pssp-materials> custom element.
 *
 * Drives the Material library section (sections/material-library.liquid):
 * - Toolbar filtering (family chips, process chips derived from card data,
 *   outdoor/jewelry toggles, use select) via the hidden attribute on cards.
 * - Results counter + PSSPTheme.announce() on every filter change.
 * - Compare flow: up to data-max-compare cards -> fixed bottom bar ->
 *   native <dialog> comparison table (Escape + backdrop close for free).
 *
 * Contract (BUILD-SPEC §0.9): customElements.get guard, lazy init via
 * PSSPTheme.onVisible (IntersectionObserver), every listener removed in
 * disconnectedCallback, motion gated through PSSPTheme.motionOK().
 * All comparison-table content is built with textContent (no innerHTML
 * from data), so merchant text can never inject markup.
 */

if (!customElements.get('pssp-materials')) {
  customElements.define(
    'pssp-materials',
    class extends HTMLElement {
      connectedCallback() {
        this._initialized = false;
        this._listeners = [];
        this._compare = [];
        this._filters = { family: '', processes: [], outdoor: false, jewelry: false, use: '' };
        this._maxCompare = parseInt(this.dataset.maxCompare, 10) || 3;
        this._dialogTrigger = null;

        /* Theme editor: reveal + scroll to the selected material block. */
        this._onBlockSelect = (event) => {
          if (!this.contains(event.target)) return;
          const card = event.target.closest('[data-material-card]');
          if (!card) return;
          if (card.hidden) this._resetFilters(false);
          card.scrollIntoView({
            behavior: window.PSSPTheme && PSSPTheme.motionOK() ? 'smooth' : 'auto',
            block: 'center'
          });
        };
        document.addEventListener('shopify:block:select', this._onBlockSelect);

        /* Lazy init: do nothing until the section nears the viewport. */
        if (window.PSSPTheme && typeof PSSPTheme.onVisible === 'function') {
          this._stopVisible = PSSPTheme.onVisible(this, () => this._init(), '250px');
        } else {
          this._stopVisible = null;
          this._init();
        }
      }

      disconnectedCallback() {
        document.removeEventListener('shopify:block:select', this._onBlockSelect);
        this._onBlockSelect = null;

        if (this._stopVisible) this._stopVisible();
        this._stopVisible = null;

        this._listeners.forEach((entry) => {
          entry[0].removeEventListener(entry[1], entry[2]);
        });
        this._listeners = [];

        if (this._dialog && this._dialog.open && typeof this._dialog.close === 'function') {
          this._dialog.close();
        }
        this._dialogTrigger = null;
        this._compare = [];
        this._cards = null;
      }

      /** addEventListener + bookkeeping so disconnectedCallback can clean up. */
      _listen(target, type, handler) {
        target.addEventListener(type, handler);
        this._listeners.push([target, type, handler]);
      }

      _announce(message) {
        if (message && window.PSSPTheme && typeof PSSPTheme.announce === 'function') {
          PSSPTheme.announce(message);
        }
      }

      /* ----- boot ----- */

      _init() {
        if (this._initialized || !this.isConnected) return;
        this._initialized = true;

        this._cards = Array.prototype.slice.call(this.querySelectorAll('[data-material-card]'));
        this._toolbar = this.querySelector('[data-materials-toolbar]');
        this._familyGroup = this.querySelector('[data-family-group]');
        this._familyChips = this.querySelector('[data-family-chips]');
        this._processGroup = this.querySelector('[data-process-group]');
        this._processChips = this.querySelector('[data-process-chips]');
        this._useSelect = this.querySelector('[data-filter-use]');
        this._countEl = this.querySelector('[data-results-count]');
        this._zeroState = this.querySelector('[data-zero-state]');
        this._bar = this.querySelector('[data-compare-bar]');
        this._namesEl = this.querySelector('[data-compare-names]');
        this._barCount = this.querySelector('[data-compare-count]');
        this._openBtn = this.querySelector('[data-compare-open]');
        this._dialog = this.querySelector('[data-compare-dialog]');
        this._tableWrap = this.querySelector('[data-compare-table]');

        if (this._cards.length === 0) return;

        this._buildFamilyChips();
        this._buildProcessChips();
        if (this._toolbar) this._toolbar.hidden = false;

        /* One delegated click handler covers every control in the section. */
        this._listen(this, 'click', (event) => this._onClick(event));

        if (this._useSelect) {
          this._listen(this._useSelect, 'change', () => {
            this._filters.use = this._useSelect.value;
            this._apply(true);
          });
        }

        if (this._dialog) {
          /* Backdrop click: the dialog element itself is the click target. */
          this._listen(this._dialog, 'click', (event) => {
            if (event.target === this._dialog) this._dialog.close();
          });
          /* Covers Escape (cancel -> close) and every other close path. */
          this._listen(this._dialog, 'close', () => {
            if (this._dialogTrigger && this._dialogTrigger.isConnected) {
              this._dialogTrigger.focus();
            }
            this._dialogTrigger = null;
          });
        }

        this._apply(false);
        this._updateCompare();
      }

      /* ----- toolbar construction (derived from card data) ----- */

      _buildFamilyChips() {
        if (!this._familyChips) return;
        const families = new Map();
        this._cards.forEach((card) => {
          const slug = card.dataset.family || '';
          if (!slug || families.has(slug)) return;
          families.set(slug, card.dataset.familyLabel || slug);
        });
        families.forEach((label, slug) => {
          this._familyChips.appendChild(this._makeChip(label, 'data-filter-family', slug));
        });
        if (families.size < 2 && this._familyGroup) this._familyGroup.hidden = true;
      }

      _buildProcessChips() {
        if (!this._processChips) return;
        const processes = new Map();
        this._cards.forEach((card) => {
          (card.dataset.processes || '').split(',').forEach((raw) => {
            const label = raw.trim();
            if (!label) return;
            const slug = label.toLowerCase();
            if (!processes.has(slug)) processes.set(slug, label);
          });
        });
        processes.forEach((label, slug) => {
          this._processChips.appendChild(this._makeChip(label, 'data-filter-process', slug));
        });
        if (this._processGroup) this._processGroup.hidden = processes.size < 2;
      }

      _makeChip(label, attribute, value) {
        const btn = document.createElement('button');
        btn.type = 'button';
        btn.className = 'material-library__chip';
        btn.setAttribute(attribute, value);
        btn.setAttribute('aria-pressed', 'false');
        btn.textContent = label;
        return btn;
      }

      /* ----- event delegation ----- */

      _onClick(event) {
        const target = event.target;

        const familyBtn = target.closest('[data-filter-family]');
        if (familyBtn && this.contains(familyBtn)) {
          this._filters.family = familyBtn.getAttribute('data-filter-family') || '';
          if (this._familyChips) {
            this._familyChips.querySelectorAll('[data-filter-family]').forEach((chip) => {
              chip.setAttribute('aria-pressed', chip === familyBtn ? 'true' : 'false');
            });
          }
          this._apply(true);
          return;
        }

        const processBtn = target.closest('[data-filter-process]');
        if (processBtn && this.contains(processBtn)) {
          const slug = processBtn.getAttribute('data-filter-process');
          const index = this._filters.processes.indexOf(slug);
          if (index === -1) {
            this._filters.processes.push(slug);
          } else {
            this._filters.processes.splice(index, 1);
          }
          processBtn.setAttribute('aria-pressed', index === -1 ? 'true' : 'false');
          this._apply(true);
          return;
        }

        const outdoorBtn = target.closest('[data-filter-outdoor]');
        if (outdoorBtn && this.contains(outdoorBtn)) {
          this._filters.outdoor = !this._filters.outdoor;
          outdoorBtn.setAttribute('aria-pressed', this._filters.outdoor ? 'true' : 'false');
          this._apply(true);
          return;
        }

        const jewelryBtn = target.closest('[data-filter-jewelry]');
        if (jewelryBtn && this.contains(jewelryBtn)) {
          this._filters.jewelry = !this._filters.jewelry;
          jewelryBtn.setAttribute('aria-pressed', this._filters.jewelry ? 'true' : 'false');
          this._apply(true);
          return;
        }

        const clearBtn = target.closest('[data-clear-filters]');
        if (clearBtn && this.contains(clearBtn)) {
          this._resetFilters(true);
          return;
        }

        const compareToggle = target.closest('[data-compare-toggle]');
        if (compareToggle && this.contains(compareToggle)) {
          const card = compareToggle.closest('[data-material-card]');
          if (card) this._toggleCompare(card);
          return;
        }

        const removeBtn = target.closest('[data-compare-remove]');
        if (removeBtn && this.contains(removeBtn)) {
          const index = parseInt(removeBtn.getAttribute('data-compare-remove'), 10);
          if (index >= 0 && index < this._compare.length) {
            this._compare.splice(index, 1);
            this._updateCompare();
          }
          return;
        }

        const openBtn = target.closest('[data-compare-open]');
        if (openBtn && this.contains(openBtn)) {
          this._openCompare();
          return;
        }

        const clearCompare = target.closest('[data-compare-clear]');
        if (clearCompare && this.contains(clearCompare)) {
          this._compare = [];
          this._updateCompare();
          return;
        }

        const closeBtn = target.closest('[data-compare-close]');
        if (closeBtn && this._dialog && this.contains(closeBtn)) {
          this._dialog.close();
        }
      }

      /* ----- filtering ----- */

      _apply(announce) {
        const f = this._filters;
        let visible = 0;

        this._cards.forEach((card) => {
          const ds = card.dataset;
          let match = true;

          if (f.family && ds.family !== f.family) match = false;

          if (match && f.processes.length > 0) {
            const own = (ds.processes || '')
              .split(',')
              .map((p) => p.trim().toLowerCase())
              .filter(Boolean);
            match = f.processes.some((p) => own.indexOf(p) !== -1);
          }

          if (match && f.outdoor && ds.outdoor !== 'true') match = false;
          if (match && f.jewelry && ds.jewelry !== 'true') match = false;

          if (match && f.use) {
            const use = ds.use || 'both';
            if (use !== 'both' && use !== f.use) match = false;
          }

          card.hidden = !match;
          if (match) visible += 1;
        });

        const template = this.dataset.resultsTemplate || '[count] / [total]';
        const text = template
          .replace('[count]', String(visible))
          .replace('[total]', String(this._cards.length));

        if (this._countEl) this._countEl.textContent = text;
        if (this._zeroState) this._zeroState.hidden = visible !== 0;

        const active = !!(f.family || f.processes.length || f.outdoor || f.jewelry || f.use);
        this.querySelectorAll('[data-clear-filters]').forEach((btn) => {
          if (btn.closest('[data-zero-state]')) return; /* always visible there */
          btn.hidden = !active;
        });

        if (announce) this._announce(text);
      }

      _resetFilters(announce) {
        this._filters = { family: '', processes: [], outdoor: false, jewelry: false, use: '' };

        if (this._familyChips) {
          this._familyChips.querySelectorAll('[data-filter-family]').forEach((chip) => {
            const isAll = (chip.getAttribute('data-filter-family') || '') === '';
            chip.setAttribute('aria-pressed', isAll ? 'true' : 'false');
          });
        }
        if (this._processChips) {
          this._processChips.querySelectorAll('[data-filter-process]').forEach((chip) => {
            chip.setAttribute('aria-pressed', 'false');
          });
        }
        this.querySelectorAll('[data-filter-outdoor], [data-filter-jewelry]').forEach((btn) => {
          btn.setAttribute('aria-pressed', 'false');
        });
        if (this._useSelect) this._useSelect.value = '';

        this._apply(announce);
      }

      /* ----- compare ----- */

      _toggleCompare(card) {
        const index = this._compare.indexOf(card);
        if (index !== -1) {
          this._compare.splice(index, 1);
        } else if (this._compare.length >= this._maxCompare) {
          this._announce(this.dataset.compareFull || '');
          return;
        } else {
          this._compare.push(card);
        }
        this._updateCompare();
      }

      _updateCompare() {
        this._cards.forEach((card) => {
          const pressed = this._compare.indexOf(card) !== -1;
          const btn = card.querySelector('[data-compare-toggle]');
          if (btn) btn.setAttribute('aria-pressed', pressed ? 'true' : 'false');
          card.classList.toggle('is-compared', pressed);
        });

        if (this._bar) this._bar.hidden = this._compare.length === 0;
        if (this._barCount) {
          this._barCount.textContent = `${this._compare.length} / ${this._maxCompare}`;
        }
        if (this._openBtn) this._openBtn.disabled = this._compare.length < 2;

        if (this._namesEl) {
          this._namesEl.textContent = '';
          const removeTemplate = this.dataset.labelRemove || 'Remove [name]';
          this._compare.forEach((card, i) => {
            const name = card.dataset.name || '';
            const chip = document.createElement('button');
            chip.type = 'button';
            chip.className = 'material-library__bar-name';
            chip.setAttribute('data-compare-remove', String(i));
            chip.setAttribute('aria-label', removeTemplate.replace('[name]', name));

            const label = document.createElement('span');
            label.textContent = name;
            chip.appendChild(label);

            const x = document.createElement('span');
            x.className = 'material-library__bar-name-x';
            x.setAttribute('aria-hidden', 'true');
            x.textContent = '×';
            chip.appendChild(x);

            this._namesEl.appendChild(chip);
          });
        }
      }

      /* ----- comparison dialog ----- */

      _openCompare() {
        if (!this._dialog || !this._tableWrap || this._compare.length < 2) return;

        this._tableWrap.textContent = '';
        this._tableWrap.appendChild(this._buildTable());

        this._dialogTrigger = document.activeElement;
        if (typeof this._dialog.showModal === 'function') {
          this._dialog.showModal();
        } else {
          this._dialog.setAttribute('open', '');
        }
      }

      _buildTable() {
        const d = this.dataset;
        const table = document.createElement('table');
        table.className = 'tech-table tech-table--compare material-library__table';

        const thead = document.createElement('thead');
        const headRow = document.createElement('tr');
        const corner = document.createElement('th');
        corner.setAttribute('scope', 'col');
        corner.textContent = d.labelAttribute || '';
        headRow.appendChild(corner);
        this._compare.forEach((card) => {
          const th = document.createElement('th');
          th.setAttribute('scope', 'col');
          th.textContent = card.dataset.name || '';
          headRow.appendChild(th);
        });
        thead.appendChild(headRow);
        table.appendChild(thead);

        const rows = [
          { label: d.labelFamily, key: 'familyLabel' },
          { label: d.labelProcess, key: 'processes' },
          { label: d.labelStrength, meter: 'strength' },
          { label: d.labelFlexibility, meter: 'flexibility' },
          { label: d.labelHeat, meter: 'heat' },
          { label: d.labelUv, meter: 'uv' },
          { label: d.labelFinish, key: 'finish' },
          { label: d.labelColors, key: 'colors' },
          { label: d.labelApplications, key: 'applications' },
          { label: d.labelLimitations, key: 'limitations' }
        ];

        const tbody = document.createElement('tbody');
        rows.forEach((row) => {
          const tr = document.createElement('tr');
          const th = document.createElement('th');
          th.setAttribute('scope', 'row');
          th.textContent = row.label || '';
          tr.appendChild(th);

          this._compare.forEach((card) => {
            const td = document.createElement('td');
            if (row.meter) {
              td.appendChild(this._buildMeter(card.dataset[row.meter]));
            } else {
              td.textContent = card.dataset[row.key] || '—';
            }
            tr.appendChild(td);
          });
          tbody.appendChild(tr);
        });
        table.appendChild(tbody);

        return table;
      }

      /** Visual 5-cell meter + visually hidden "n of 5" for screen readers. */
      _buildMeter(raw) {
        const value = Math.min(Math.max(parseInt(raw, 10) || 0, 0), 5);
        const frag = document.createDocumentFragment();

        const track = document.createElement('span');
        track.className = 'material-card__meter-track';
        track.setAttribute('aria-hidden', 'true');
        for (let i = 1; i <= 5; i += 1) {
          const cell = document.createElement('span');
          cell.className = 'material-card__meter-cell' + (i <= value ? ' is-on' : '');
          track.appendChild(cell);
        }
        frag.appendChild(track);

        const sr = document.createElement('span');
        sr.className = 'visually-hidden';
        sr.textContent = `${value} ${this.dataset.labelOf || 'of'} 5`;
        frag.appendChild(sr);

        return frag;
      }
    }
  );
}
